import React, { useCallback, useMemo } from 'react';
import { useClientUser } from '@/hooks';

import {
    CARRIER_RATE_ERROR,
    CONSIGNEELESS_ORDER_TYPES,
    EDIT_ORDER_TABS,
    hiddenTabsForOrderType,
    PROGRESSION_TYPES,
    ROUTE_VALIDATION_ERROR,
    SHIPMENT_FORM_MODAL_PROGRESSION,
} from '../constants';
import { genAttributes, validate, defaultRequiredFields } from '@onward-delivery/core';
import { genAttributesPerTab } from './modal';
import { useServiceCatalogs } from './serviceCatalog';
import {
    getInvalidServiceLines,
    getMissingServiceCatalogFields,
    getServiceCatalogForOrder,
} from '../utilities/serviceCatalog';
import { flattenDeclaredPallet } from '../utilities/pallets';
import { omit } from 'lodash';
import { ITEM_READONLY_FIELDS, ORDER_READONLY_FIELDS, SERVICE_READONLY_FIELDS } from '@onward-delivery/core';

// Saved customer leg needs coords + street
export const isOperatorResolvedDropoff = (order) => {
    if (!order) return false;

    const contact = genAttributes(order);
    const hasCoordinates = !!(order[contact.lat] && order[contact.long]);
    const hasStreet = !!order[contact.street];
    const geocodeSucceeded = !order[contact.geocode_failed];

    return hasCoordinates && hasStreet && geocodeSucceeded;
};

const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
};

const normalizeAddressPart = (value) => {
    return (value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
};

const legHasNormalizedAddressMismatch = (order, attrs) => {
    const street = normalizeAddressPart(order[attrs.street]);
    const address = normalizeAddressPart(order[attrs.address]);
    if (!street || !address) return false;

    // Don't require an exact substring: the full address may abbreviate or expand
    // the street suffix ("St" vs "Street"), which would false-flag a legitimate
    // match. Compare significant tokens only (house number + street-name words,
    // ignoring 1-2 char suffixes) and flag a mismatch only when none appear.
    const addressTokens = new Set(address.split(' '));
    const streetTokens = street.split(' ').filter((token) => token.length > 2 || /\d/.test(token));
    return streetTokens.length > 0 && !streetTokens.some((token) => addressTokens.has(token));
};

const legHasAddressIssue = (order, attrs) => {
    return (
        [attrs.street, attrs.city, attrs.state, attrs.zip, attrs.lat, attrs.long].some(
            (field) => !hasValue(order[field])
        ) || legHasNormalizedAddressMismatch(order, attrs)
    );
};

export const useOrderErrors = (orders) => {
    const { user_id } = useClientUser();
    const catalogFallback = useMemo(
        () => ({
            shipperId: orders?.[0]?.shipper_id || user_id,
        }),
        [orders, user_id]
    );
    const { catalogs, errors } = useServiceCatalogs(orders, catalogFallback);

    return useMemo(() => {
        if (orders.length === 0) {
            return {};
        }

        return Object.fromEntries(
            orders.map((order) => {
                const serviceCatalog = getServiceCatalogForOrder(order, catalogs, catalogFallback);
                const missingCatalogFields = getMissingServiceCatalogFields(order, catalogs, errors, catalogFallback);
                const contact = genAttributes(order);
                const pickup = genAttributes(order, true);
                const fieldsByTab = genAttributesPerTab({
                    contact,
                    pickup,
                });

                // Settlement clients require carrier_rate; add it to validate so it flows through `missing`.
                const requireCarrierRate =
                    !!order.shipper_rate_override?.pricing_parameters?.require_explicit_carrier_pricing;
                const [, hasMissingField] = validate(
                    order,
                    requireCarrierRate ? [...defaultRequiredFields(order), 'carrier_rate'] : [],
                    { invalidServiceLines: getInvalidServiceLines(order, serviceCatalog) }
                );
                // validate() misses saved-dropoff resolution fields
                const geocodeFailed = [
                    ...(!CONSIGNEELESS_ORDER_TYPES.includes(order.order_type) && !isOperatorResolvedDropoff(order)
                        ? [contact.lat]
                        : []),
                    ...(order[pickup.geocode_failed] ? [pickup.lat] : []),
                ];
                const missing = [...new Set([...(hasMissingField || []), ...missingCatalogFields, ...geocodeFailed])];

                // Tabs hidden for this order type can't carry a user-reachable error, so drop
                // them — otherwise a customer-bucket failure would badge a tab that no longer exists.
                const hiddenTabs = hiddenTabsForOrderType(order.order_type);
                const byTab = Object.fromEntries(
                    Object.keys(fieldsByTab)
                        .filter((tab) => !hiddenTabs.includes(tab))
                        .map((tab) => [tab, fieldsByTab[tab].some((field) => missing?.includes(field))])
                );
                const hasMileageIssue = missing.includes('miles');
                const pickupHasAddressIssue = hasMileageIssue && legHasAddressIssue(order, pickup);
                const deliveryHasAddressIssue = hasMileageIssue && legHasAddressIssue(order, contact);
                const hasLegSpecificMileageIssue = pickupHasAddressIssue || deliveryHasAddressIssue;

                return [
                    order.order_id,
                    {
                        ...byTab,
                        ...(pickupHasAddressIssue ? { [EDIT_ORDER_TABS.PICKUP]: true } : {}),
                        ...(deliveryHasAddressIssue ? { [EDIT_ORDER_TABS.CUSTOMER]: true } : {}),
                        [ROUTE_VALIDATION_ERROR]: hasMileageIssue && !hasLegSpecificMileageIssue,
                        [CARRIER_RATE_ERROR]: missing.includes('carrier_rate'),
                    },
                ];
            }, [])
        );
    }, [orders, catalogs, errors, catalogFallback]);
};

export const useJobDetailsCallbacks = (state, callbacks) => {
    const { user_id, isOnwardAdmin, payment_type, stripe_payment_method } = useClientUser();

    const advance = useCallback(() => {
        if (state.progression.current === SHIPMENT_FORM_MODAL_PROGRESSION[state.progression.type].length - 1) {
            callbacks.confirmOrders({
                variables: {
                    order_ids: state.job.orders.map((order) => order.order_id),
                },
            });

            callbacks.setProgression({
                current: 0,
                type: null,
            });
        } else {
            callbacks.setProgression((prev) => {
                return { ...prev, current: prev.current + 1 };
            });
        }
    }, [state, callbacks]);

    const revert = useCallback(() => {
        callbacks.setProgression((prev) => {
            if (prev.current > 0) {
                return { ...prev, current: prev.current - 1 };
            }

            return {
                current: 0,
                type: null,
            };
        });
    }, []);

    const closeEditModal = useCallback(() => {
        callbacks.setEditing(null);
    }, []);

    const openEditModal = useCallback((order, tab, tabOffset = 0) => {
        callbacks.setEditing({ order, tab, tabOffset });
    }, []);

    const closeEditPickupModal = useCallback(() => {
        callbacks.setEditingPickup(null);
    }, []);

    const openEditPickupModal = useCallback((orders) => {
        callbacks.setEditingPickup({ orders });
    }, []);

    const deleteOrder = useCallback((order) => {
        callbacks.deleteOrder({
            variables: {
                order_id: order.order_id,
            },
        });
    }, []);

    const confirmOrders = useCallback(() => {
        const containsDuplicates = state.job.orders.some((order) => order.order_status === 'duplicate');
        const isMarketplace = state.job.orders.some((order) => !order.oms);

        if (!containsDuplicates && !isMarketplace) {
            callbacks.confirmOrders({
                variables: {
                    order_ids: state.job.orders.map((order) => order.order_id),
                },
            });
        }

        if (containsDuplicates && isMarketplace) {
            callbacks.setProgression((prev) => {
                return {
                    ...prev,
                    type: PROGRESSION_TYPES.PO_CONFIRM_FINALIZE_MARKETPLACE_JOB,
                };
            });
        } else if (containsDuplicates) {
            callbacks.setProgression((prev) => {
                return {
                    ...prev,
                    type: PROGRESSION_TYPES.PO_CONFIRM,
                };
            });
        } else if (isMarketplace) {
            callbacks.setProgression((prev) => {
                return {
                    ...prev,
                    type: PROGRESSION_TYPES.FINALIZE_MARKETPLACE_JOB,
                };
            });
        }
    }, [state.job]);

    const overrideDuplicates = useCallback(() => {
        callbacks.overrideDuplicates({
            variables: {
                order_ids: state.job.orders.map((order) => order.order_id),
            },
            onCompleted: () => {
                advance();
            },
        });
    }, [state, callbacks]);

    const clearDuplicates = useCallback(() => {
        callbacks.clearDuplicates({
            variables: {
                order_ids: state.job.orders.map((order) => order.order_id),
            },
            onCompleted: () => {
                advance();
            },
        });
    }, [state, callbacks]);

    const submitOrder = useCallback((order) => {
        submitOrders([order]);
    }, []);

    const submitOrders = useCallback((orders) => {
        const first = orders[0];
        const client_id = (first.oms && first.carrier_id) || first.shipper_id || user_id;
        const service_removals = orders.flatMap((order) => order.servicesRemoved || []);
        callbacks.submitOrders({
            client_id: client_id,
            orders: orders.map((order) => {
                const { itemsByOrderId, notes, services, references, servicesRemoved, ...remaining } = order;
                return {
                    ...omit(remaining, ORDER_READONLY_FIELDS),
                    ...(notes?.length ? { notes: notes } : {}),
                    ...(Array.isArray(services)
                        ? { services: services.map((service) => omit(service, SERVICE_READONLY_FIELDS)) }
                        : {}),
                    ...(Array.isArray(references)
                        ? {
                              references: references
                                  .filter((ref) => ref?.key && ref?.value)
                                  .map(({ key, value, label }) => ({ key, value, label })),
                          }
                        : {}),
                    itemsByOrderId: (itemsByOrderId || []).map((item) =>
                        // The 'pallet' exclusion is load-bearing here: flattenDeclaredPallet reads
                        // item.pallet off this omitted object to build the flat declaration.
                        flattenDeclaredPallet(
                            omit(
                                item,
                                ITEM_READONLY_FIELDS.filter((field) => field !== 'pallet')
                            )
                        )
                    ),
                };
            }),
            service_removals,
            type: 'MANUAL',
        });
    }, []);

    return {
        revert,
        advance,
        openEditModal,
        closeEditModal,
        openEditPickupModal,
        closeEditPickupModal,
        deleteOrder,
        confirmOrders,
        submitOrder,
        submitOrders,
        overrideDuplicates,
        clearDuplicates,
    };
};
